import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import TopBar from "../components/TopBar";
import { Card, Button, Chip, Skeleton, Empty } from "../components/ui";
import { api, apiError, fmtDate } from "../lib/api";
import { toast } from "sonner";
import MyStatsCard from "../components/MyStatsCard";
import AttendanceCard from "../components/AttendanceCard";
import PendingPaymentsCard from "../components/PendingPaymentsCard";
import ResetRequestsCard from "../components/ResetRequestsCard";
import ClubLeadersCard from "../components/ClubLeadersCard";
import ShareJoinStepsButton from "../components/ShareJoinStepsButton";
import {
  PlusCircle,
  Users,
  Receipt,
  Trophy,
  Copy,
  Calendar,
  MapPin,
  Vote,
  Users2,
  AlertCircle,
  Info,
  Lock,
} from "lucide-react";

// Admin-only quick actions (owner + manager)
const QUICK_ADMIN = [
  { label: "New Poll", icon: Vote, to: "/polls/new", testid: "qa-new-poll" },
  { label: "Add Match", icon: Trophy, to: "/matches?new=1", testid: "qa-add-match" },
  { label: "Add Expense", icon: Receipt, to: "/expenses/new", testid: "qa-add-expense" },
  { label: "View Members", icon: Users, to: "/members", testid: "qa-view-members" },
];

// Member quick actions
const QUICK_MEMBER = [
  { label: "Matches", icon: Trophy, to: "/matches", testid: "qa-view-matches" },
  { label: "Polls", icon: Vote, to: "/polls", testid: "qa-view-polls" },
  { label: "Members", icon: Users, to: "/members", testid: "qa-view-members" },
  { label: "Profile", icon: Users2, to: "/profile", testid: "qa-view-profile" },
];

export default function HomePage() {
  const nav = useNavigate();
  const { user, club } = useAuth();
  const isAdmin = ["owner", "admin", "manager"].includes(club?.my_role);
  const QUICK = isAdmin ? QUICK_ADMIN : QUICK_MEMBER;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0); // bumps the stats in the black box after a payment

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard");
      setData(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copyJoinCode = () => {
    if (!club?.join_code) return;
    navigator.clipboard?.writeText(club.join_code);
    toast.success(`Join code ${club.join_code} copied`);
  };

  return (
    <div className="pb-24">
      <TopBar
        subtitle={`Hi, ${user?.name?.split(" ")[0] || "there"}`}
        title={club?.name || "Your Club"}
        right={
          club?.join_code && (
            <button
              onClick={copyJoinCode}
              className="flex items-center gap-1.5 h-9 px-3 rounded-full bg-black/5 text-xs font-semibold btn-press"
              data-testid="copy-join-code-btn"
            >
              <Copy size={13} />
              {club.join_code}
            </button>
          )
        }
      />

      {/* Hero: my cricket */}
      <MyStatsCard refreshKey={refreshKey} />

      {user && !user.mobile && (
        <div className="px-5 mt-4">
          <button
            onClick={() => nav("/profile")}
            className="w-full text-left flex items-center gap-3 rounded-2xl bg-amber-50 border border-amber-200/70 px-4 py-3 btn-press"
            data-testid="add-mobile-prompt"
          >
            <span className="text-xl" aria-hidden>📱</span>
            <span className="flex-1 text-sm text-amber-900">
              <b>Add your mobile number</b> so your club can reach you on WhatsApp.
            </span>
            <span className="text-sm font-semibold text-amber-800">Add</span>
          </button>
        </div>
      )}

      <PendingPaymentsCard
        total={data?.my_pending_total}
        charges={data?.my_pending_charges || []}
        club={club}
        onPaid={() => { load(); setRefreshKey((k) => k + 1); }}
      />

      {isAdmin && <ResetRequestsCard />}

      {isAdmin && club?.join_code && (
        <div className="px-5 mt-6">
          <Card>
            <div className="font-semibold">Invite players with your club code</div>
            <p className="text-sm text-ink-muted mt-0.5 mb-3">
              Send your group the steps to join — they enter code <span className="font-mono font-bold text-ink">{club.join_code}</span> and they're in.
            </p>
            <ShareJoinStepsButton club={club} />
          </Card>
        </div>
      )}

      {/* Quick actions */}
      <div className="px-5 mt-6">
        <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold mb-3">
          Quick actions
        </div>
        <div className="grid grid-cols-4 gap-3">
          {QUICK.map(({ label, icon: Icon, to, testid }) => (
            <button
              key={label}
              onClick={() => nav(to)}
              data-testid={testid}
              className="flex flex-col items-center gap-2 py-3 rounded-2xl bg-white border border-black/[0.06] shadow-card btn-press"
            >
              <div className="h-10 w-10 rounded-2xl bg-brand-50 text-brand grid place-items-center">
                <Icon size={20} strokeWidth={2} />
              </div>
              <span className="text-[11px] font-semibold text-ink text-center leading-tight">
                {label}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Upcoming poll (Smart Match Poll) */}
      {data?.upcoming_poll && (
        <div className="px-5 mt-6">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold">
              Live match poll
            </div>
            <button
              onClick={() => nav("/polls")}
              className="text-xs font-semibold text-brand"
              data-testid="see-all-polls"
            >
              All polls
            </button>
          </div>
          <Card
            onClick={() => nav(`/polls/${data.upcoming_poll.id}`)}
            className="cursor-pointer"
            data-testid="upcoming-poll-card"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Chip tone="success"><Vote size={11}/> POLL</Chip>
                  <Chip tone="dark" className="capitalize">{data.upcoming_poll.match_type}</Chip>
                </div>
                <h3 className="font-bold text-lg mt-2 truncate">{data.upcoming_poll.title}</h3>
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-sm text-ink-muted">
                  <span className="flex items-center gap-1"><Calendar size={13}/>{fmtDate(data.upcoming_poll.date)}{
                    data.upcoming_poll.start_time && data.upcoming_poll.end_time
                      ? ` • ${data.upcoming_poll.start_time.substring(0, 5)}–${data.upcoming_poll.end_time.substring(0, 5)}`
                      : data.upcoming_poll.time ? ` • ${data.upcoming_poll.time}` : ""
                  }</span>
                  <span className="flex items-center gap-1"><MapPin size={13}/>{data.upcoming_poll.ground}</span>
                </div>
                <div className="flex items-center gap-3 mt-3 text-[13px] font-semibold">
                  <span className="text-brand flex items-center gap-1"><Users2 size={13}/> {data.upcoming_poll.counts?.in || 0} IN</span>
                  <span className="text-red-500">{data.upcoming_poll.counts?.out || 0} OUT</span>
                  <span className="text-amber-600">{data.upcoming_poll.counts?.maybe || 0} MAYBE</span>
                </div>
              </div>
            </div>
          </Card>
        </div>
      )}

      {isAdmin && <AttendanceCard />}

      {/* Upcoming match */}
      <div className="px-5 mt-6">
        <div className="flex items-center justify-between mb-3">
          <div className="text-xs uppercase tracking-widest text-ink-muted font-semibold">
            Upcoming match
          </div>
          <button
            onClick={() => nav("/matches")}
            className="text-xs font-semibold text-brand"
            data-testid="see-all-matches"
          >
            See all
          </button>
        </div>
        {loading ? (
          <Skeleton className="h-24" />
        ) : data?.upcoming_match ? (
          <Card
            onClick={() => nav(`/matches/${data.upcoming_match.id}`)}
            className="cursor-pointer"
            data-testid="upcoming-match-card"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <Chip tone="success">{data.upcoming_match.format}</Chip>
                <h3 className="font-bold text-lg mt-2">vs {data.upcoming_match.opponent}</h3>
                <div className="flex items-center gap-3 mt-2 text-sm text-ink-muted">
                  <span className="flex items-center gap-1"><Calendar size={13} />{fmtDate(data.upcoming_match.date)}</span>
                  <span className="flex items-center gap-1"><MapPin size={13} />{data.upcoming_match.ground}</span>
                </div>
              </div>
              <div className="text-right">
                <div className="text-[11px] uppercase tracking-widest text-ink-muted font-semibold">Overs</div>
                <div className="text-xl font-bold">{data.upcoming_match.overs}</div>
              </div>
            </div>
          </Card>
        ) : (
          <Card className="p-0">
            <Empty
              icon={Trophy}
              title="No upcoming matches"
              subtitle="Schedule your next fixture."
            />
            <div className="p-4 pt-0">
              <Button onClick={() => nav("/matches?new=1")} className="w-full" data-testid="empty-add-match-btn">
                <PlusCircle size={16} /> Add match
              </Button>
            </div>
          </Card>
        )}
      </div>

      <ClubLeadersCard />
    </div>
  );
}
